import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFinance } from "./FinanceContext";
import { sumTo } from "./data/model";
import { MONTHS } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { MonthPickerRow } from "./components/MonthPicker";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import { toneColor } from "./components/tone";

export function ProfitAndLossPage() {
  const { model, fmt, selMonth } = useFinance();

  const revByMonth = Array(12).fill(0);
  model.revenue.forEach((r) => r.actual.forEach((v, m) => (revByMonth[m] += v)));
  const rev = sumTo(revByMonth, selMonth);

  let direct = 0;
  let opex = 0;
  model.items.forEach((it) => {
    const a = sumTo(it.actual, selMonth);
    if (it.type === "Direct") direct += a;
    else opex += a;
  });
  const gross = rev - direct;
  const ebit = gross - opex;

  const rowsP: [string, number, "pos" | "neg"][] = [
    ["Revenue", rev, "pos"],
    ["Less: Direct / Manufacturing (COGS)", -direct, "neg"],
    ["Gross Profit", gross, gross >= 0 ? "pos" : "neg"],
    ["Less: Operating Expenses", -opex, "neg"],
    ["Operating Profit (EBITDA approx)", ebit, ebit >= 0 ? "pos" : "neg"],
  ];

  const kpis: KpiItem[] = [
    { label: "Revenue (YTD)", value: fmt(rev) },
    { label: "Gross Profit", value: fmt(gross), tone: gross >= 0 ? "pos" : "neg" },
    { label: "Operating Profit", value: fmt(ebit), tone: ebit >= 0 ? "pos" : "neg" },
    { label: "Net Margin", value: (rev ? (ebit / rev) * 100 : 0).toFixed(1) + "%" },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Profit &amp; Loss Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        YTD through {MONTHS[selMonth]} · actuals-based
      </Typography>
      <AnnualBar />
      <MonthPickerRow />
      <KpiGrid items={kpis} />
      <TableContainer sx={{ maxWidth: 640 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Line Item</TableCell>
              <TableCell align="right">Amount (YTD)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsP.map(([label, value, tone]) => {
              const isProfitRow = /Profit/.test(label);
              return (
                <TableRow
                  key={label}
                  sx={
                    isProfitRow
                      ? { "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }
                      : undefined
                  }
                >
                  <TableCell>{label}</TableCell>
                  <TableCell align="right" sx={{ color: toneColor(tone) }}>
                    {fmt(value)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
